import React from "react";
import { portfolio } from "@/data/portfolio";
import useReveal from "@/hooks/useReveal";
import { ArrowUpRight } from "lucide-react";

const Plate = ({ item, idx, isLast }) => {
  const ref = useReveal();
  return (
    <article
      data-testid={`product-${idx}`}
      className={isLast ? "" : "hairline-b"}
      style={{ borderColor: "var(--rule)" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 pt-12 md:pt-16 pb-16 md:pb-24">
        {/* Left: Sticky Roman Numeral */}
        <div className="md:col-span-1">
          <div className="md:sticky md:top-24 romnum text-3xl md:text-4xl" style={{ color: "var(--accent)" }}>{item.roman}</div>
        </div>

        {/* Right Content: Full-width panel card with details underneath */}
        <div ref={ref} className="reveal-up md:col-span-11">
          <div className="rounded-2xl overflow-hidden border flex flex-col" style={{ borderColor: "var(--rule)", background: "var(--paper)" }}>
            
            {/* Top Visual Panel */}
            <div 
              className="relative w-full aspect-[21/9] min-h-[220px] sm:min-h-[280px] md:min-h-[340px] flex flex-col justify-between p-6 md:p-8 overflow-hidden"
              style={{
                background: item.accent,
              }}
            >
              {item.image ? (
                <>
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="absolute inset-0 w-full h-full z-0 transition-transform duration-[1.5s] hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-black/70 z-10" />
                </>
              ) : (
                <div
                  className="absolute inset-0 pointer-events-none z-10"
                  style={{
                    background:
                      "radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.16), rgba(0,0,0,0) 60%), radial-gradient(120% 80% at 100% 100%, rgba(0,0,0,0.25), rgba(0,0,0,0) 60%)",
                  }}
                />
              )}

              {/* Panel Top Header */}
              <div 
                className="relative z-20 font-mono text-[0.65rem] sm:text-[0.7rem] uppercase tracking-microcap text-white"
                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.95)" }}
              >
                PLATE {item.roman} &nbsp;&bull;&nbsp; {item.plate} &nbsp;&bull;&nbsp; {item.kicker}
              </div>

              {/* Panel Center Wordmark */}
              <div 
                className="relative z-20 font-serif text-3xl sm:text-5xl md:text-6xl leading-none tracking-tightest text-white mt-auto mb-auto"
                style={{ textShadow: "0 4px 16px rgba(0,0,0,0.95)" }}
              >
                {item.wordmark}
              </div>

              {/* Panel Bottom Footer */}
              <div 
                className="relative z-20 font-mono text-[0.65rem] sm:text-[0.7rem] uppercase tracking-microcap text-white"
                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.95)" }}
              >
                {item.name} &nbsp;&bull;&nbsp; {item.status}
              </div>
            </div>

            {/* Bottom Info Grid */}
            <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10" style={{ background: "var(--paper-2)" }}>
              {/* Left Column: Metadata & Visit Link */}
              <div className="md:col-span-4 space-y-6">
                <div>
                  <div className="font-mono text-[0.65rem] uppercase tracking-microcap text-accent mb-2">
                    {item.plate} &bull; PLATE {item.roman}
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl tracking-tightest leading-tight">{item.name}</h3>
                </div>

                <div className="border-t border-b py-4 space-y-2.5" style={{ borderColor: "var(--rule)" }}>
                  <div className="flex justify-between font-mono text-[0.65rem] uppercase tracking-microcap">
                    <span className="opacity-50">Role</span>
                    <span style={{ color: "var(--ink)" }}>{item.kicker}</span>
                  </div>
                  <div className="flex justify-between font-mono text-[0.65rem] uppercase tracking-microcap">
                    <span className="opacity-50">Status</span>
                    <span style={{ color: "var(--ink)" }}>{item.status}</span>
                  </div>
                </div>

                <div>
                  <a
                    href={item.url}
                    target={item.url && item.url.startsWith("http") ? "_blank" : undefined}
                    rel={item.url && item.url.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="inline-flex items-center gap-1 text-accent font-mono text-[0.72rem] uppercase tracking-microcap border-b border-accent pb-0.5 hover:opacity-75 transition-opacity"
                  >
                    visit project <ArrowUpRight size={13} />
                  </a>
                </div>
              </div>

              {/* Right Column: Quote & Description */}
              <div className="md:col-span-8 space-y-6">
                <div className="border-l-2 pl-4 italic font-serif text-xl sm:text-2xl leading-relaxed text-accent">
                  &ldquo;{item.quote}&rdquo;
                </div>
                <p className="font-serif text-base sm:text-lg leading-relaxed max-w-[62ch]" style={{ color: "var(--ink-2)" }}>
                  {item.body}
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {item.tags.map((t, i) => (
                    <span
                      key={i}
                      className="font-mono text-[0.65rem] uppercase tracking-microcap px-2.5 py-1 rounded-full border"
                      style={{ borderColor: "var(--rule)", color: "var(--ink-muted)" }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </article>
  );
};

const Products = () => {
  const { products } = portfolio;
  return (
    <section
      id="products"
      data-testid="products-section"
      className="px-5 md:px-10 py-14 md:py-28"
      style={{ background: "var(--paper-2)" }}
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="flex items-baseline justify-between mb-6 md:mb-10">
          <div className="section-tag">
            <span style={{ color: "var(--ink)" }}>{products.section}</span>
            <span className="ml-3">{products.eyebrow}</span>
          </div>
          <div className="section-tag">→ Plates B.01 — B.04</div>
        </div>

        <h2 className="display text-4xl sm:text-5xl md:text-7xl lg:text-8xl max-w-[18ch]">
          Four products, all{" "}
          <span className="display-italic" style={{ color: "var(--accent)" }}>mine</span>, all shipping.
        </h2>

        <p className="font-serif text-base sm:text-lg md:text-xl max-w-[60ch] mt-6 md:mt-8 mb-10 md:mb-20" style={{ color: "var(--ink-2)" }}>
          {products.intro}
        </p>

        <div>
          {products.plates.map((p, i) => (
            <Plate key={p.plate} item={p} idx={i} isLast={i === products.plates.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
